import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import { ITileData } from 'apps/placeme/src/features/Analyse/slice/@types/ITileData'
import { ENDPOINTS } from 'apps/placeme/src/constants/endpoints'
import { demoId } from '@dataplace.ai/constants'
import { McdPotentialTile } from './McdPotentialModelTile'
import { useFormFieldsData } from './DemoSalesPredictionModelData/hooks/formData/useFormFieldData'
import { useSelectedSettings } from './DemoSalesPredictionModelData/hooks/resultData/useSelectedSettings'
import { useGetChartData } from './DemoSalesPredictionModelData/hooks/resultData/useGetChartData'
import { radarChartLabelsDemo } from './DemoSalesPredictionModelData/hooks/resultData/radarChartLabelsDemo'

export interface IPotentialModelSettingsDemo {
  locationType: string,
  locationAdditions: string[],
  plannedShoppingMall?: boolean,
  plannedShoppingMallSize?: string,
  plannedShoppingMallFoodCourtExists?: boolean,
}

export interface Influence {
  [key: string]: number
}

export interface IDemoSalesPredictionModel extends ITileData {
  value: {
    potentialModel: {
      transactions: number,
      averageChainTransactions: number,
      averageChainSales: number,
      sales: number,
      influence: Influence,
    }
    settings: IPotentialModelSettingsDemo
  }
}

export type FormFieldValue = number | string | boolean | null;

export enum FormFieldsDemo {
  locationType = 'locationType',
  locationAdditions = 'locationAdditions',
  plannedShoppingMall = 'plannedShoppingMall',
  plannedShoppingMallSize = 'plannedShoppingMallSize',
  plannedShoppingMallFoodCourtExists = 'plannedShoppingMallFoodCourtExists',
}

export interface InitialFormStateDemo {
  locationType: string
  plannedShoppingMall: boolean
  plannedShoppingMallSize: string
  plannedShoppingMallFoodCourtExists: boolean
  roadSimulation: boolean
}

const initialState = {
  locationType: '',
  plannedShoppingMall: false,
  plannedShoppingMallSize: 'medium',
  plannedShoppingMallFoodCourtExists: false,
  roadSimulation: false,
}

export const DemoSalesPredictionModel: React.FC<{
  data: IDemoSalesPredictionModel,
  tileId: string,
}> = ({
  data, tileId,
}) => {
  const { values } = useSelector((state: RootState) => state.analysis)

  const [locationAdditions, setLocationAdditions] = useState<string[]>([])
  const [formState, setFormState] = useState<InitialFormStateDemo>(initialState)

  const barData = useGetChartData(data)
  const settingList = useSelectedSettings(data)
  const formDataObject = useFormFieldsData(formState, formState.plannedShoppingMall)

  const setFormValue = useCallback((key: string, value: FormFieldValue) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }))
  }, [setFormState])

  const catchmentId = useMemo(() => values.find(cat =>
    cat.id === demoId)?.tiles.find(t => t.id === tileId)?.chosenRange?.catchmentId, [values, tileId])

  const body = useMemo(() => ({
    catchmentId,
    locationType: formState.locationType,
    locationAdditions,
    plannedShoppingMall: formState.plannedShoppingMall,
    plannedShoppingMallSize: formState.plannedShoppingMallSize,
    plannedShoppingMallFoodCourtExists: formState.plannedShoppingMallFoodCourtExists,
  }), [formState, catchmentId])

  return (
    <McdPotentialTile
      barData={barData}
      body={body}
      categoryId={demoId}
      data={data}
      endpoint={ENDPOINTS.MCD_DEMO_SALES_PREDICTION}
      formDataObject={formDataObject}
      formState={formState}
      labels={radarChartLabelsDemo}
      setFormValue={setFormValue}
      setLocationAdditions={setLocationAdditions}
      settingList={settingList}
      tileId={tileId}
    />
  )
}
